import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { checkRateLimit, getClientIp } from "@/lib/rate-limit.server";
import { formatRateLimitMessage } from "@/lib/rate-limit-message";

const InquirySchema = z.object({
  email: z.string().trim().email("Please enter a valid email").max(255),
  website: z.string().max(0).optional().or(z.literal("")), // honeypot
});

export type TeamInquiryResult =
  | { ok: true }
  | {
      ok: false;
      error: string;
      code: "validation" | "server" | "config" | "rate_limited";
      fieldErrors?: Record<string, string>;
      retryAfterSeconds?: number;
    };

export const submitTeamInquiry = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => {
    const parsed = InquirySchema.safeParse(input);
    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (typeof key === "string" && !fieldErrors[key]) {
          fieldErrors[key] = issue.message;
        }
      }
      throw new Error(JSON.stringify({ kind: "validation", fieldErrors }));
    }
    return parsed.data;
  })
  .handler(async ({ data }): Promise<TeamInquiryResult> => {
    // Rate-limit gate 1: per-IP
    const ip = getClientIp();
    const ipCheck = await checkRateLimit({
      name: "team-ip",
      identifier: ip,
      windowSeconds: 60,
      maxRequests: 3,
    });
    if (!ipCheck.allowed) {
      return {
        ok: false,
        code: "rate_limited",
        error: formatRateLimitMessage(ipCheck.retryAfterSeconds),
        retryAfterSeconds: ipCheck.retryAfterSeconds,
      };
    }

    // Honeypot: silently succeed
    if (data.website && data.website.length > 0) {
      return { ok: true };
    }

    // Rate-limit gate 2: per-email
    const emailCheck = await checkRateLimit({
      name: "team-email",
      identifier: data.email.toLowerCase().trim(),
      windowSeconds: 60,
      maxRequests: 3,
    });
    if (!emailCheck.allowed) {
      return {
        ok: false,
        code: "rate_limited",
        error: formatRateLimitMessage(emailCheck.retryAfterSeconds),
        retryAfterSeconds: emailCheck.retryAfterSeconds,
      };
    }

    const submittedAt = new Date().toISOString();

    // 1. Save to Supabase (admin client — bypasses RLS)
    const { error: dbError } = await supabaseAdmin
      .from("team_inquiries")
      .insert({
        email: data.email,
        source: "corporate-page-comp-code",
      });

    if (dbError) {
      console.error("[teams] insert failed", dbError);
      return {
        ok: false,
        code: "server",
        error: "Something went wrong. Please email hello@whoknew.guide directly.",
      };
    }

    // 2. Send operator notification via Lovable Custom Emails
    try {
      const { enqueueTransactionalEmail } = await import("@/lib/email/send.server");

      const operatorEmail = process.env.CONTACT_FORM_TO_EMAIL;
      if (!operatorEmail) {
        console.error("[teams] CONTACT_FORM_TO_EMAIL not set — row saved without notification");
        return { ok: true };
      }

      const result = await enqueueTransactionalEmail({
        templateName: "team-inquiry-notification",
        recipientEmail: operatorEmail,
        templateData: {
          submitterEmail: data.email,
          submittedAt,
        },
        replyTo: data.email,
        bypassSuppression: true,
      });

      if (!result.success) {
        console.error("[teams] enqueue failed:", result);
        // Row saved — don't bounce the user.
      }
    } catch (err) {
      console.error("[teams] enqueue threw:", err);
    }

    return { ok: true };
  });
